import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from '@aimsos/i18n';

import {
  assignMemberGroup,
  getMemberGroups,
  listGroups,
  removeMemberGroup,
  validateApiResponse,
} from '../../../services';
import { UNDO_WINDOW_MS } from '../constants/groupMembers';
import { MEMBER_STATUS } from '../constants/copy';
import { mapApiGroupToUi } from '../utils/groupMappers';
import { useMemberTabData } from './useMemberTabData';
import { useMemberTabMutation } from './useMemberTabMutation';
import { useToastRef } from './useToastRef';

/**
 * Groups of a member and the groups still to join: `{ groups, assignable, isLoading, error }` plus
 * `assign(groupId)` and `remove(group)`. `remove` is immediate with an Undo toast (the same
 * behavior as removing a member from the group detail): Undo joins the group again. A failed
 * change toasts an error and resolves `false`.
 *
 * @param {{ id: string, displayName: string, status: string }} member UI member
 */
export const useMemberGroups = (member) => {
  const { t } = useTranslation('people-access');
  const toastRef = useToastRef();
  const memberId = member.id;
  const removingRef = useRef(new Set());

  const load = useCallback(async () => {
    const [mine, catalog] = await Promise.all([getMemberGroups(memberId), listGroups()]);
    const groups = validateApiResponse(mine, t('memberTabs.loadError')).body.items.map(mapApiGroupToUi);
    const all = validateApiResponse(catalog, t('memberTabs.loadError')).body.items;
    const taken = new Set(groups.map((g) => g.id));
    return { groups, assignable: all.filter((g) => !taken.has(g.id)).map(mapApiGroupToUi) };
  }, [memberId, t]);
  const { data, isLoading, error, refresh, retry } = useMemberTabData(memberId, load, {
    enabled: member.status !== MEMBER_STATUS.REMOVED,
  });

  const { mutate, notifyRefreshFailed } = useMemberTabMutation({
    refresh,
    failedTitleKey: 'memberTabs.groups.toast.failedTitle',
    toastRef,
  });

  const assign = useCallback((groupId) => mutate(() => assignMemberGroup(memberId, groupId)), [mutate, memberId]);

  /**
   * Undo: join the group again. A group the member already re-joined by hand counts as restored.
   * The assign answer is read as is (no `validateApiResponse`): the 409 has a meaning here.
   */
  const undoRemoval = useCallback(
    async (group) => {
      try {
        const res = await assignMemberGroup(memberId, group.id);
        const alreadyIn = res?.status_code === 409 && res.msg_code?.[0]?.code === 'ALREADY_ASSIGNED';
        if (res?.status_code === 200 || alreadyIn) {
          if ((await refresh()) === false) notifyRefreshFailed();
          return;
        }
      } catch {
        // A thrown request is a failed Undo like any other.
      }
      toastRef.current.error({
        title: t('memberTabs.groups.toast.undoFailedTitle'),
        message: t('memberTabs.groups.toast.undoFailedMessage', { name: member.displayName, group: group.name }),
      });
    },
    [memberId, member.displayName, refresh, notifyRefreshFailed, toastRef, t],
  );

  const remove = useCallback(
    async (group) => {
      // One request per group: a double click must not send a second DELETE that would fail.
      if (removingRef.current.has(group.id)) return false;
      removingRef.current.add(group.id);
      try {
        const ok = await mutate(() => removeMemberGroup(memberId, group.id));
        if (!ok) return false;
        // The message names the group, so two quick removals never collapse into one toast.
        const toastId = toastRef.current.info({
          title: t('memberTabs.groups.toast.removedTitle'),
          message: t('memberTabs.groups.toast.removedMessage', { group: group.name }),
          action: t('memberTabs.groups.toast.undo'),
          duration: UNDO_WINDOW_MS,
          onAction: () => {
            toastRef.current.close(toastId);
            undoRemoval(group);
          },
        });
        return true;
      } finally {
        removingRef.current.delete(group.id);
      }
    },
    [mutate, undoRemoval, memberId, toastRef, t],
  );

  return useMemo(
    () => ({
      groups: data?.groups ?? [],
      assignable: data?.assignable ?? [],
      isLoading,
      error,
      assign,
      remove,
      reload: retry,
    }),
    [data, isLoading, error, assign, remove, retry],
  );
};
